if (!customElements.get('mm-info-drawer')) {
  customElements.define(
    'mm-info-drawer',
    class MMInfoDrawer extends HTMLElement {
      _portalToBody() {
        if (this.parentElement === document.body) return;

        if (!this._portalAnchor) {
          this._portalAnchor = document.createComment('mm-info-drawer-anchor');
          this.parentNode?.insertBefore(this._portalAnchor, this);
        }

        document.body.appendChild(this);
      }

      _restoreFromPortal() {
        if (!this._portalAnchor?.parentNode) return;
        this._portalAnchor.parentNode.insertBefore(this, this._portalAnchor);
        this._portalAnchor.remove();
        this._portalAnchor = null;
      }

      _markBannerLoaded(img) {
        if (!img) return;
        img.classList.add('is-loaded');
        img.dataset.mmPreloaded = 'true';
      }

      _preloadBannerImage(img) {
        if (!img || img.dataset.mmPreloaded === 'true') return;

        if (img.complete && img.naturalWidth > 0) {
          this._markBannerLoaded(img);
          return;
        }

        const src = img.currentSrc || img.src;
        if (!src) return;

        const loader = new Image();
        loader.onload = () => this._markBannerLoaded(img);
        loader.src = src;
      }

      _preloadPanel(handle) {
        if (!handle || !this.panels?.length) return;
        const panel = [...this.panels].find((p) => p.dataset.mmInfoPanel === handle);
        this._preloadBannerImage(panel?.querySelector('.mm-info-drawer__banner-image'));
      }

      _preloadAllBanners() {
        this.querySelectorAll('.mm-info-drawer__banner-image').forEach((img) => {
          img.addEventListener('load', () => this._markBannerLoaded(img), { once: true });
          this._preloadBannerImage(img);
        });
      }

      connectedCallback() {
        this._portalToBody();

        this.sectionId = this.dataset.mmInfoDrawer;
        this.closeButtons = this.querySelectorAll('[data-mm-info-close]');
        this.openButtons = document.querySelectorAll(`[data-mm-info-open="${this.sectionId}"]`);
        this.panels = this.querySelectorAll('[data-mm-info-panel]');

        this.handleOpen = this.open.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleClose = this.close.bind(this);
        this.handleBackdropClick = this._onBackdropClick.bind(this);
        this.handlePreload = (event) => {
          this._preloadPanel(event.currentTarget?.dataset?.mmProductHandle);
        };

        this.openButtons.forEach((button) => {
          button.addEventListener('click', this.handleOpen);
          button.addEventListener('pointerenter', this.handlePreload, { passive: true });
          button.addEventListener('focusin', this.handlePreload);
        });

        this._preloadAllBanners();

        this.closeButtons.forEach((button) => {
          button.addEventListener('click', this.handleClose);
        });

        this.addEventListener('click', this.handleBackdropClick);
      }

      disconnectedCallback() {
        this.openButtons?.forEach((button) => {
          button.removeEventListener('click', this.handleOpen);
          button.removeEventListener('pointerenter', this.handlePreload);
          button.removeEventListener('focusin', this.handlePreload);
        });

        this.closeButtons?.forEach((button) => {
          button.removeEventListener('click', this.handleClose);
        });

        this.removeEventListener('click', this.handleBackdropClick);
        document.removeEventListener('keydown', this.handleKeydown);
        this._restoreFromPortal();
      }

      _onBackdropClick(event) {
        if (!this.classList.contains('active')) return;
        const inner = this.querySelector('.mm-info-drawer__inner');
        if (inner && event.target.closest('.mm-info-drawer__inner')) return;
        this.close();
      }

      open(event) {
        const target = event?.currentTarget;
        const handle = target?.dataset?.mmProductHandle;
        this._preloadPanel(handle);
        this.setActivePanel(handle);
        this.classList.add('active');
        document.addEventListener('keydown', this.handleKeydown);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.modalToggle) {
          publish(PUB_SUB_EVENTS.modalToggle, { display: false });
        }
        if (typeof window.toggleChatIcon === 'function') {
          window.toggleChatIcon({ display: false });
        }
        if (typeof lockBodyScroll === 'function') lockBodyScroll();

        this.querySelector('.mm-info-drawer__inner')?.focus();
      }

      setActivePanel(handle) {
        if (!this.panels?.length) return;
        let hasMatch = false;

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.mmInfoPanel === handle;
          panel.classList.toggle('hidden', !isActive);
          if (isActive) hasMatch = true;
        });

        if (!hasMatch) {
          this.panels.forEach((panel, index) => {
            panel.classList.toggle('hidden', index !== 0);
          });
        }
      }

      close() {
        if (!this.classList.contains('active')) return;
        this.classList.remove('active');
        document.removeEventListener('keydown', this.handleKeydown);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.modalToggle) {
          publish(PUB_SUB_EVENTS.modalToggle, { display: true });
        }
        if (typeof window.toggleChatIcon === 'function') {
          window.toggleChatIcon({ display: true });
        }
        if (typeof unlockBodyScroll === 'function') unlockBodyScroll();
      }

      onKeydown(event) {
        if (event.code === 'Escape') this.close();
      }
    }
  );
}
